import apiClient from './client';
import type { Course, CourseEdition, CourseWithEditions } from '../types';

// ─── Public ───────────────────────────────────────────────────────────────────

export async function getPublicCourses(): Promise<CourseWithEditions[]> {
  const response = await apiClient.get<{ courses: CourseWithEditions[] }>('/courses');
  return response.data.courses;
}

// ─── Admin: Courses ───────────────────────────────────────────────────────────

export async function getAdminCourses(): Promise<Course[]> {
  const response = await apiClient.get<{ courses: Course[] }>('/admin/courses');
  return response.data.courses;
}

export async function createCourse(data: {
  name: string;
  slug: string;
  description?: string;
  active?: boolean;
}): Promise<Course> {
  const response = await apiClient.post<{ course: Course }>('/admin/courses', data);
  return response.data.course;
}

export async function updateCourse(
  id: string,
  data: Partial<{
    name: string;
    slug: string;
    description: string;
    active: boolean;
    contactEmail: string | null;
    showSpeakers: boolean;
  }>
): Promise<Course> {
  const response = await apiClient.put<{ course: Course }>(`/admin/courses/${id}`, data);
  return response.data.course;
}

// ─── Admin: Editions ──────────────────────────────────────────────────────────

export async function getAdminEditions(): Promise<CourseEdition[]> {
  const response = await apiClient.get<{ editions: CourseEdition[] }>('/admin/editions');
  return response.data.editions;
}

export async function createEdition(data: {
  courseId: string;
  label: string;
  zoomMeetingId?: string;
  zoomMeetingUrl?: string;
  scheduledAt: string;
  durationMinutes: number;
  maxParticipants: number;
  registrationOpen?: boolean;
  inPresenza?: boolean;
  waitlistEnabled?: boolean;
}): Promise<CourseEdition> {
  const response = await apiClient.post<{ edition: CourseEdition }>('/admin/editions', data);
  return response.data.edition;
}

export async function updateEdition(
  id: string,
  data: Partial<{
    courseId: string;
    label: string;
    zoomMeetingId: string;
    zoomMeetingUrl: string;
    scheduledAt: string;
    durationMinutes: number;
    maxParticipants: number;
    registrationOpen: boolean;
    inPresenza: boolean;
  }>
): Promise<CourseEdition> {
  const response = await apiClient.put<{ edition: CourseEdition }>(`/admin/editions/${id}`, data);
  return response.data.edition;
}

export async function updateCourseFields(courseId: string, fields: import('../types').RegistrationField[]): Promise<void> {
  await apiClient.put(`/admin/courses/${courseId}/fields`, { fields });
}

export async function exportEnrollments(editionId: string): Promise<void> {
  const res = await apiClient.get(`/admin/editions/${editionId}/enrollments/export`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `iscritti-${editionId.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function uploadCourseBanner(courseId: string, file: File): Promise<{ bannerUrl: string }> {
  const formData = new FormData();
  formData.append('banner', file);
  const response = await apiClient.post(`/admin/courses/${courseId}/banner`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { bannerUrl: response.data.course.banner_url };
}

export async function deleteCourseBanner(courseId: string): Promise<void> {
  await apiClient.delete(`/admin/courses/${courseId}/banner`);
}

export async function uploadCourseBadgeBg(courseId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('badge_bg', file);
  await apiClient.post(`/admin/courses/${courseId}/badge-bg`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function deleteCourseBadgeBg(courseId: string): Promise<void> {
  await apiClient.delete(`/admin/courses/${courseId}/badge-bg`);
}

export async function uploadCourseEmailBanner(courseId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('email_banner', file);
  await apiClient.post(`/admin/courses/${courseId}/email-banner`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function deleteCourseEmailBanner(courseId: string): Promise<void> {
  await apiClient.delete(`/admin/courses/${courseId}/email-banner`);
}

export async function uploadCourseProgramma(courseId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('programma', file);
  await apiClient.post(`/admin/courses/${courseId}/programma`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function deleteCourseProgramma(courseId: string): Promise<void> {
  await apiClient.delete(`/admin/courses/${courseId}/programma`);
}

export async function syncZoom(editionId: string): Promise<{
  success: boolean;
  meetingId: string;
  zoomRegistrantCount: number;
  syncResults: Record<string, number>;
}> {
  const response = await apiClient.get(`/admin/editions/${editionId}/sync-zoom`);
  return response.data;
}

export async function sendReminders(editionId: string): Promise<{ queued: number }> {
  const response = await apiClient.post(`/admin/editions/${editionId}/send-reminders`);
  return response.data;
}
